import React, { useState, useRef, useEffect } from "react";
import { useLocation, useNavigate } from "react-router-dom";
import { ArrowRight, ChevronDown } from "lucide-react";
import { EnhancedTokenManager } from "../../../utils/tokenManager";
import { SubdomainManager } from "../../../utils/subdomainUtils";
import { ReactComponent as BackButton } from "../../../assets/icons/svg/onbording/back-button.svg";
import { ReactComponent as Logo } from "../../../assets/icons/svg/Cluva/Logo-with-text.svg";

import { ReactComponent as OnboardingImage } from "../../../assets/icons/svg/onbording/OnboardingImage.svg";
import { ReactComponent as Home } from "../../../assets/icons/svg/onbording/home.svg";
import { ReactComponent as Folder } from "../../../assets/icons/svg/onbording/folder.svg";
import { ReactComponent as Setting } from "../../../assets/icons/svg/onbording/setting.svg";
import { ReactComponent as Logout } from "../../../assets/icons/svg/onbording/logout.svg";
import { ReactComponent as Workspace } from "../../../assets/icons/svg/onbording/workspace.svg";
import { ReactComponent as UploadIcon } from "../../../assets/icons/svg/onbording/upload-onboarding-icon.svg";
import { ReactComponent as UndoIcon } from "../../../assets/icons/svg/onbording/undo-icon.svg";
import { ReactComponent as Check } from "../../../assets/icons/svg/onbording/tick.svg";
import { ReactComponent as Cancel } from "../../../assets/icons/svg/onbording/cancel.svg";
import DotLoader from "../../../assets/Animation/Dot Loader.json";
import Lottie from "lottie-react";
import { DEFAULT_FONT_FAMILY } from "../../../config/fontConfig";
import Button from "../../ui/buttons";

import IconPNG1 from "../../../assets/icons/png/onboarding-icon1.png";
import IconPNG2 from "../../../assets/icons/png/onboarding-icon2.png";
import IconPNG3 from "../../../assets/icons/png/onboarding-icon3.png";
import IconPNG4 from "../../../assets/icons/png/onboarding-icon4.png";

// Import API URLs from urls.ts file
import { API_URLS } from "../../../api/urls";
import useDocumentTitle from "../../../hooks/useDocumentTitle";

// Types for API responses
interface OnboardingRequest {
  type: "Individual";
  purpose: "Work" | "Personal";
  role:
    | "Founder"
    | "Product Owner"
    | "Product Manager"
    | "Designer"
    | "Business Analyst"
    | "DevOps"
    | "Developer"
    | "QA"
    | "Technical Writer"
    | "Other";
  workspace_name: string;
  subdomain: string;
  collaborators?: string[];
}

interface OnboardingApiResponse {
  success: boolean;
  message?: string;
  data?: {
    message: string;
    workspace: {
      id: number;
      name: string;
      subdomain: string;
    };
    user_id?: number;
    [key: string]: any;
  };
}

interface RefreshTokenResponse {
  access_token: string;
  refresh_token?: string;
  token_type?: string;
}

interface AuthTokens {
  access_token: string;
  refresh_token: string;
  token_type: string;
}

/**
 * Token Storage Manager
 * Handles secure token storage and retrieval
 */
class TokenManager {
  private static ACCESS_TOKEN_KEY = "access_token";
  private static REFRESH_TOKEN_KEY = "refresh_token";

  static setTokens(tokens: AuthTokens) {
    try {
      localStorage.setItem(this.ACCESS_TOKEN_KEY, tokens.access_token);
      localStorage.setItem(this.REFRESH_TOKEN_KEY, tokens.refresh_token);
      console.log("Tokens stored successfully");
    } catch (error) {
      console.warn("Could not store tokens:", error);
    }
  }

  static getAccessToken(): string | null {
    try {
      return localStorage.getItem(this.ACCESS_TOKEN_KEY);
    } catch (error) {
      console.warn("Could not retrieve access token:", error);
      return null;
    }
  }

  static getRefreshToken(): string | null {
    try {
      return localStorage.getItem(this.REFRESH_TOKEN_KEY);
    } catch (error) {
      console.warn("Could not retrieve refresh token:", error);
      return null;
    }
  }

  static clearTokens() {
    try {
      localStorage.removeItem(this.ACCESS_TOKEN_KEY);
      localStorage.removeItem(this.REFRESH_TOKEN_KEY);
      console.log("Tokens cleared");
    } catch (error) {
      console.warn("Could not clear tokens:", error);
    }
  }
}

/**
 * API Service Class for handling authentication and API calls
 */
class ApiService {
  private static isRefreshing = false;
  private static failedQueue: Array<{
    resolve: (value: any) => void;
    reject: (reason: any) => void;
  }> = [];

  private static processQueue(error: any, token: string | null = null) {
    this.failedQueue.forEach(({ resolve, reject }) => {
      if (error) {
        reject(error);
      } else {
        resolve(token);
      }
    });

    this.failedQueue = [];
  }

  static async refreshAccessToken(): Promise<boolean> {
    const refreshToken = TokenManager.getRefreshToken();

    if (!refreshToken) {
      console.log("No refresh token available");
      return false;
    }

    if (this.isRefreshing) {
      return new Promise((resolve, reject) => {
        this.failedQueue.push({ resolve, reject });
      });
    }

    this.isRefreshing = true;

    try {
      console.log("Attempting to refresh access token...");
      const response = await fetch(API_URLS.REFRESH_TOKEN, {
        method: "POST",
        headers: {
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          refresh_token: refreshToken,
        }),
      });

      console.log("Refresh token response status:", response.status);

      if (response.ok) {
        const data: RefreshTokenResponse = await response.json();
        console.log("Token refresh successful");

        const newTokens: AuthTokens = {
          access_token: data.access_token,
          refresh_token: data.refresh_token || refreshToken,
          token_type: data.token_type || "bearer",
        };

        TokenManager.setTokens(newTokens);
        this.processQueue(null, data.access_token);

        return true;
      } else {
        const errorData = await response.json();
        console.error("Refresh token failed:", errorData);

        TokenManager.clearTokens();
        this.processQueue(errorData, null);

        return false;
      }
    } catch (error) {
      console.error("Error refreshing token:", error);
      this.processQueue(error, null);
      return false;
    } finally {
      this.isRefreshing = false;
    }
  }

  static async makeAuthenticatedRequest(
    url: string,
    options: RequestInit = {}
  ): Promise<Response> {
    const accessToken = TokenManager.getAccessToken();

    if (!accessToken) {
      throw new Error("No access token available. Please log in again.");
    }

    console.log(`Making authenticated request to: ${url}`);

    const headers = {
      "Content-Type": "application/json",
      Authorization: `Bearer ${accessToken}`,
      ...options.headers,
    };

    let response = await fetch(url, {
      ...options,
      headers,
    });

    console.log(`Initial response status: ${response.status}`);

    if (response.status === 401) {
      console.log("Received 401, attempting token refresh...");

      const refreshed = await this.refreshAccessToken();

      if (refreshed) {
        const newAccessToken = TokenManager.getAccessToken();
        console.log(
          "Token refreshed successfully, retrying original request..."
        );

        response = await fetch(url, {
          ...options,
          headers: {
            ...headers,
            Authorization: `Bearer ${newAccessToken}`,
          },
        });

        console.log(`Retry response status: ${response.status}`);
      } else {
        throw new Error("Authentication failed. Please log in again.");
      }
    }

    return response;
  }

  static async checkWorkspaceName(subdomain: string): Promise<{
    success: boolean;
    available: boolean;
    message?: string;
  }> {
    try {
      console.log("Checking subdomain availability:", subdomain);

      const response = await this.makeAuthenticatedRequest(
        API_URLS.CHECK_SUBDOMAIN(subdomain),
        {
          method: "GET",
        }
      );

      console.log("Subdomain check response status:", response.status);

      if (response.ok) {
        const data = await response.json();
        console.log("Subdomain check response:", data);

        return {
          success: true,
          available: data.available,
          message: data.message,
        };
      } else {
        const errorData = await response.json();
        console.error("Subdomain check failed:", errorData);
        return {
          success: false,
          available: false,
          message:
            errorData.message ||
            errorData.detail ||
            `Request failed with status ${response.status}`,
        };
      }
    } catch (error) {
      console.error("Subdomain check error:", error);

      if (
        error instanceof Error &&
        error.message.includes("Authentication failed")
      ) {
        return {
          success: false,
          available: false,
          message: "Authentication expired. Please log in again.",
        };
      }

      return {
        success: false,
        available: false,
        message:
          error instanceof Error ? error.message : "Network error occurred",
      };
    }
  }

  static async submitOnboarding(
    data: OnboardingRequest
  ): Promise<OnboardingApiResponse> {
    try {
      console.log("Submitting onboarding data:", data);

      const response = await this.makeAuthenticatedRequest(
        API_URLS.ONBOARDING,
        {
          method: "POST",
          body: JSON.stringify(data),
        }
      );

      console.log("Onboarding response status:", response.status);

      if (response.ok) {
        const result = await response.json();
        console.log("Onboarding success:", result);

        if (result.workspace?.id) {
          console.log(
            "✅ Workspace ID received from API:",
            result.workspace.id
          );
        } else {
          console.warn("⚠️ No workspace.id in API response:", result);
        }

        return { success: true, data: result };
      } else {
        const errorData = await response.json();
        console.error("Onboarding failed:", errorData);
        return {
          success: false,
          message:
            errorData.message ||
            errorData.detail?.message ||
            errorData.detail ||
            `Request failed with status ${response.status}`,
        };
      }
    } catch (error) {
      console.error("Onboarding API Error:", error);

      if (
        error instanceof Error &&
        error.message.includes("Authentication failed")
      ) {
        return {
          success: false,
          message: "Authentication expired. Please log in again.",
        };
      }

      return {
        success: false,
        message:
          error instanceof Error ? error.message : "Network error occurred",
      };
    }
  }
}

/**
 * ✅ NEW: Function to upload workspace icon separately after onboarding
 */
const uploadWorkspaceIconSeparately = async (
  userId: number,
  workspaceId: number,
  uploadedIconDataUrl: string | null,
  selectedIconId: string | null,
  accessToken: string
): Promise<boolean> => {
  try {
    if (!uploadedIconDataUrl && !selectedIconId) {
      console.log("No icon selected, skipping upload");
      return true;
    }

    console.log("📤 Uploading workspace icon separately...");

    const formData = new FormData();
    formData.append("workspace_id", workspaceId.toString());

    let finalBlob: Blob | null = null;
    let fileName = "";

    // ✅ FIXED: Handle predefined icons properly
    if (selectedIconId && selectedIconId !== "uploaded") {
      const iconMapping: { [key: string]: string } = {
        icon1: IconPNG1,
        icon2: IconPNG2,
        icon3: IconPNG3,
        icon4: IconPNG4,
      };

      const iconSrc = iconMapping[selectedIconId];
      if (iconSrc) {
        try {
          console.log("🖼️ Using predefined icon:", selectedIconId);

          // ✅ CRITICAL FIX: Fetch as blob with proper MIME type
          const response = await fetch(iconSrc);

          if (!response.ok) {
            console.error("Failed to fetch predefined icon:", response.status);
            return false;
          }

          finalBlob = await response.blob();

          // ✅ CRITICAL FIX: Force PNG MIME type if not already set
          if (
            !finalBlob.type ||
            finalBlob.type === "application/octet-stream"
          ) {
            finalBlob = new Blob([finalBlob], { type: "image/png" });
          }

          fileName = `workspace_icon_${workspaceId}_${Date.now()}.png`;

          console.log("✅ Predefined icon prepared:");
          console.log("   - Icon ID:", selectedIconId);
          console.log("   - Type:", finalBlob.type);
          console.log("   - Size:", finalBlob.size, "bytes");
          console.log("   - Filename:", fileName);
        } catch (error) {
          console.error("Could not fetch predefined icon:", error);
          return false;
        }
      }
    }
    // Handle uploaded custom icon
    else if (uploadedIconDataUrl && uploadedIconDataUrl.startsWith("data:")) {
      try {
        const blob = await fetch(uploadedIconDataUrl).then((res) => res.blob());

        // Extract file extension from MIME type
        const mimeType = blob.type || "image/png";
        const fileExtension = mimeType.split("/")[1] || "png";

        // ✅ Ensure proper MIME type
        finalBlob = new Blob([blob], { type: mimeType });
        fileName = `workspace_icon_${workspaceId}_${Date.now()}.${fileExtension}`;

        console.log("✅ Custom uploaded icon prepared:");
        console.log("   - Type:", finalBlob.type);
        console.log("   - Size:", finalBlob.size, "bytes");
        console.log("   - Filename:", fileName);
      } catch (error) {
        console.error("Error processing uploaded icon:", error);
        return false;
      }
    } else {
      console.warn("No valid icon data to upload");
      return false;
    }

    // ✅ Add the properly formatted blob to FormData
    if (finalBlob) {
      formData.append("icon_url", finalBlob, fileName);

      console.log("📦 FormData prepared with:");
      console.log("   - workspace_id:", workspaceId);
      console.log(
        "   - icon_url:",
        fileName,
        `(${finalBlob.type}, ${finalBlob.size} bytes)`
      );
    } else {
      console.error("No blob to upload");
      return false;
    }

    // Upload to API
    const response = await fetch(
      API_URLS.UPDATE_PROFILE_AND_WORKSPACE_URL,
      {
        method: "POST",
        headers: {
          Authorization: `Bearer ${accessToken}`,
        },
        body: formData,
      }
    );

    if (!response.ok) {
      const errorText = await response.text();
      console.error("❌ Icon upload failed:", response.status, errorText);
      return false;
    }

    const result = await response.json();
    console.log("✅ Icon uploaded successfully:", result);

    // Update workspace icon in token manager if available
    if (result.data?.workspace?.icon_url) {
      const currentUserId = EnhancedTokenManager.getCurrentUserId();
      if (currentUserId) {
        EnhancedTokenManager.setWorkspaceIconForUser(
          currentUserId,
          result.data.workspace.icon_url
        );
        console.log("✅ Workspace icon updated in token manager");
      }
    }

    return true;
  } catch (error) {
    console.error("❌ Error uploading icon:", error);
    return false;
  }
};

/**
 * Shimmer Component for loading effects
 */
const Shimmer = ({ className }: { className: string }) => (
  <div
    className={`animate-pulse ${className}`}
    style={{
      background:
        "linear-gradient(90deg, #f0f0f0 25%, #e0e0e0 50%, #f0f0f0 75%)",
      backgroundSize: "200% 100%",
      animation: "shimmer 2s ease-in-out infinite",
    }}
  />
);

/**
 * Step 1 Shimmer - Radio buttons and dropdown
 */
const Step1Shimmer = () => (
  <div>
    <style>
      {`
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}
    </style>
    <div className="mb-10">
      <Shimmer className="h-8 xl:h-10 2xl:h-12 w-80 2xl:w-96 rounded-lg mb-2" />
      <Shimmer className="h-8 xl:h-10 2xl:h-12 w-60 2xl:w-72 rounded-lg" />
    </div>

    <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 mb-6">
      {[1, 2].map((i) => (
        <div key={i} className="flex items-center gap-2">
          <Shimmer className="w-5 h-5 rounded-full" />
          <Shimmer className="h-4 xl:h-5 2xl:h-6 w-20 2xl:w-24 rounded" />
        </div>
      ))}
    </div>
  </div>
);

/**
 * Step 2 Shimmer - Workspace naming
 */
const Step2Shimmer = () => (
  <div>
    <style>
      {`
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}
    </style>
    <Shimmer className="h-8 xl:h-10 2xl:h-12 w-72 2xl:w-80 rounded-lg mb-4" />

    <div className="mb-6">
      <Shimmer className="h-4 xl:h-5 2xl:h-6 w-full max-w-md rounded mb-2" />
      <Shimmer className="h-4 xl:h-5 2xl:h-6 w-80 2xl:w-96 rounded" />
    </div>

    <Shimmer className="h-12 xl:h-14 2xl:h-16 w-[400px] 2xl:w-[500px] rounded-lg mb-4" />
  </div>
);

/**
 * Step 3 Shimmer - Icon selection
 */
const Step3Shimmer = () => (
  <div>
    <style>
      {`
        @keyframes shimmer {
          0% { background-position: -200% 0; }
          100% { background-position: 200% 0; }
        }
      `}
    </style>
    <Shimmer className="h-8 xl:h-10 2xl:h-12 w-80 2xl:w-96 rounded-lg mb-4" />

    <Shimmer className="h-4 xl:h-5 2xl:h-6 w-72 2xl:w-80 rounded mb-6" />

    <div className="grid grid-cols-5 gap-4 mb-6 max-w-sm 2xl:max-w-lg">
      {[1, 2, 3, 4, 5].map((i) => (
        <Shimmer key={i} className="w-16 h-16 2xl:w-20 2xl:h-20 rounded-xl" />
      ))}
    </div>
  </div>
);

/**
 * Custom Radio Button Component
 */
const RadioOption = ({
  value,
  label,
  selectedOption,
  onChange,
  fontFamily,
}: {
  value: string;
  label: string;
  selectedOption: string;
  onChange: (value: string) => void;
  fontFamily: string;
}) => {
  const isSelected = selectedOption === value;

  return (
    <label className="flex items-center gap-2 cursor-pointer">
      <div className="relative w-5 h-5">
        <input
          type="radio"
          value={value}
          checked={isSelected}
          onChange={() => onChange(value)}
          className="sr-only"
        />

        <div
          className={`w-5 h-5 rounded-full border-2 flex items-center justify-center transition-colors duration-300 ${
            isSelected ? "border-[#6BA4D8]" : "border-[#6BA4D8]"
          }`}
        >
          <div
            className={`w-2.5 h-2.5 rounded-full flex items-center justify-center ${
              isSelected ? "bg-white" : "bg-transparent"
            }`}
          >
            <div
              className={`w-2.5 h-2.5 rounded-full ${
                isSelected ? "bg-[#6BA4D8]" : "bg-transparent"
              }`}
            />
          </div>
        </div>
      </div>

      <span 
        className="text-sm xl:text-base 3xl:text-2xl font-medium text-[#424650]"
        style={{ fontFamily }}
      >
        {label}
      </span>
    </label>
  );
};

/**
 * Main Onboarding Screen Component
 */
const OnboardingScreen: React.FC = () => {
  useDocumentTitle("Onboarding");
  const navigate = useNavigate();
  const location = useLocation();

  // ✅ Use centralized font configuration
  const fontFamily = DEFAULT_FONT_FAMILY;

  const baseDomain = SubdomainManager.getBaseDomain();

  // ✅ NEW: Extract return URL from navigation state
  const locationState = location.state as {
    email?: string;
    fullName?: string;
    fromSignUp?: boolean;
    fromSignIn?: boolean;
    returnUrl?: string;
    returnState?: any;
    error?: string;
  } | null;

  // Loading states
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isInitializing, setIsInitializing] = useState(false);
  const [isCheckingWorkspace, setIsCheckingWorkspace] = useState(false);

  // Step 1: Usage and Role selection state
  const [selectedUsage, setSelectedUsage] = useState("");
  const [selectedRole, setSelectedRole] = useState("");
  const [showRoleOptions, setShowRoleOptions] = useState(false);

  // Step 2: Workspace naming state
  const [workspaceName, setWorkspaceName] = useState("");
  const [subdomain, setSubdomain] = useState("");
  const [workspaceNameAvailable, setWorkspaceNameAvailable] = useState<
    boolean | null
  >(null);

  // Navigation state
  const [currentStep, setCurrentStep] = useState(1);

  // Step 3: Icon selection state
  const [selectedIcon, setSelectedIcon] = useState<string | null>(null);
  const [uploadedIcon, setUploadedIcon] = useState<string | null>(null);

  // Error handling
  const [error, setError] = useState<string | null>(null);

  // File upload reference
  const fileInputRef = useRef<HTMLInputElement>(null);

  // Debounce timer
  const debounceTimer = useRef<NodeJS.Timeout | null>(null);

  // Available role options for dropdown
  const roleOptions = [
    "Founder",
    "Product Owner",
    "Product Manager",
    "Designer",
    "Business Analyst",
    "DevOps",
    "Developer",
    "QA",
    "Technical Writer",
    "Other",
  ];

  // Predefined workspace icons
  const pngIcons = [
    { src: IconPNG1, id: "icon1" },
    { src: IconPNG2, id: "icon2" },
    { src: IconPNG3, id: "icon3" },
    { src: IconPNG4, id: "icon4" },
  ];

  // Initialize tokens on component mount
  useEffect(() => {
    const initializeTokens = async () => {
      setIsInitializing(true);

      try {
        const accessToken = TokenManager.getAccessToken();
        const refreshToken = TokenManager.getRefreshToken();

        if (!accessToken || !refreshToken) {
          console.warn(
            "No authentication tokens found. User may need to log in again."
          );
          setError("Authentication required. Please log in again.");
        } else {
          console.log("Tokens found and ready for use");
        }
      } catch (error) {
        console.error("Error initializing tokens:", error);
        setError("Failed to initialize authentication. Please try again.");
      } finally {
        setTimeout(() => {
          setIsInitializing(false);
        }, 100);
      }
    };

    initializeTokens();
  }, []);

  // ✅ NEW: Restore deep link info from sessionStorage if page was refreshed
  useEffect(() => {
    if (!locationState?.returnUrl) {
      const pendingDeepLink = sessionStorage.getItem("pending_deep_link");

      if (pendingDeepLink) {
        try {
          const deepLinkInfo = JSON.parse(pendingDeepLink);
          console.log(
            "🔄 Restored pending deep link from sessionStorage:",
            deepLinkInfo
          );

          (window as any).__pending_deep_link_info = {
            returnUrl: `/view-card/${deepLinkInfo.cardId}?fromDeepLink=true`,
            returnState: {
              cardId: deepLinkInfo.cardId,
              fromDeepLink: true,
              returnToDashboard: true,
              developerEmail: deepLinkInfo.developerEmail,
              activeTab: "all",
            },
          };
        } catch (error) {
          console.error("Failed to restore deep link info:", error);
        }
      }
    }
  }, [locationState?.returnUrl]);

  // Reference to track if we've processed the URL selection flag
  const hasProcessedUrlSelection = useRef(false);

  // Check workspace name availability with debounce (after user stops typing)
  useEffect(() => {
    if (currentStep === 2 && subdomain.trim().length > 0) {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }

      debounceTimer.current = setTimeout(async () => {
        const fullSubdomain = `${subdomain.trim()}.${SubdomainManager.getBaseDomain()}`;
        console.log(
          "🔍 Checking subdomain availability via API:",
          fullSubdomain
        );
        setIsCheckingWorkspace(true);
        setError(null);

        const result = await ApiService.checkWorkspaceName(fullSubdomain);

        if (result.success) {
          if (result.available) {
            console.log("✅ Subdomain is available");
            setWorkspaceNameAvailable(true);
            setError(null);
          } else {
            console.log("❌ Subdomain unavailable");
            setWorkspaceNameAvailable(false);
            if (result.message) {
              setError(result.message);
            }
          }
        } else {
          setWorkspaceNameAvailable(false);
          setError(result.message || "Failed to check subdomain");
        }

        setIsCheckingWorkspace(false);
      }, 1000);
    } else if (currentStep === 2 && subdomain.trim().length === 0) {
      setWorkspaceNameAvailable(null);
      setError(null);
    }

    return () => {
      if (debounceTimer.current) {
        clearTimeout(debounceTimer.current);
      }
    };
  }, [subdomain, currentStep]);

  const canContinue =
    (currentStep === 1 && selectedUsage && selectedRole) ||
    (currentStep === 2 &&
      workspaceName.trim().length > 0 &&
      subdomain.trim().length > 0 &&
      workspaceNameAvailable === true) ||
    (currentStep === 3 && (selectedIcon || uploadedIcon));

  const convertFileToUrl = (file: File): Promise<string> => {
    return new Promise((resolve, reject) => {
      const reader = new FileReader();
      reader.onload = (e) => {
        const result = e.target?.result as string;
        resolve(result);
      };
      reader.onerror = () => reject(new Error("Failed to read file"));
      reader.readAsDataURL(file);
    });
  };

  const mapToApiFormat = (): OnboardingRequest => {
    const purpose = selectedUsage === "work" ? "Work" : "Personal";
    const role = selectedRole as
      | "Founder"
      | "Product Owner"
      | "Product Manager"
      | "Designer"
      | "Business Analyst"
      | "DevOps"
      | "Developer"
      | "QA"
      | "Technical Writer"
      | "Other";

    const fullSubdomain = `${subdomain.trim()}.${SubdomainManager.getBaseDomain()}`;

    return {
      type: "Individual",
      purpose,
      role,
      workspace_name: workspaceName.trim(),
      subdomain: fullSubdomain,
      collaborators: [],
    };
  };

  const handleOnboardingComplete = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      const apiData = mapToApiFormat();
      console.log("📝 Submitting onboarding data (without icon):", apiData);

      const result = await ApiService.submitOnboarding(apiData);

      if (result.success && result.data) {
        console.log("Onboarding completed successfully:", result.data);

        const workspaceId = result.data.workspace?.id;
        const userId = EnhancedTokenManager.getCurrentUserId();
        const responseWorkspaceName =
          result.data.workspace?.name || workspaceName.trim();
        const subdomain = result.data.workspace?.subdomain;

        if (workspaceId && userId) {
          console.log("🚀 Workspace created with ID:", workspaceId);
          console.log("👤 User ID extracted from token:", userId);

          // Store workspace data
          localStorage.setItem("workspace_id", workspaceId.toString());

          // ✅ NEW: Upload icon separately AFTER onboarding success
          const accessToken = TokenManager.getAccessToken();
          if (accessToken && (selectedIcon || uploadedIcon)) {
            console.log("📤 Starting separate icon upload...");

            const iconUploaded = await uploadWorkspaceIconSeparately(
              userId,
              workspaceId,
              uploadedIcon,
              selectedIcon,
              accessToken
            );

            if (iconUploaded) {
              console.log("✅ Workspace icon uploaded successfully");
            } else {
              console.warn("⚠️ Icon upload failed, but onboarding completed");
            }
          } else {
            console.log(
              "ℹ️ No icon selected or no access token, skipping icon upload"
            );
          }

          const workspaceData = {
            id: workspaceId,
            name: responseWorkspaceName,
            subdomain: subdomain,
            icon_url: "",
          };

          const currentUserId = EnhancedTokenManager.getCurrentUserId();
          if (currentUserId) {
            EnhancedTokenManager.setWorkspaceDataForUser(
              currentUserId,
              workspaceData
            );

            const existingProfile = EnhancedTokenManager.getUserProfile();
            if (existingProfile) {
              const updatedProfile = {
                ...existingProfile,
                onboarded: true,
                workspace_id: workspaceId,
                workspace: workspaceData,
              };
              EnhancedTokenManager.setUserProfile(updatedProfile);
            }
          }

          EnhancedTokenManager.setOnboardingWorkspaceData({
            workspace: result.data.workspace,
            icon_url: "",
            user_id: userId,
          });

          localStorage.setItem("showBetaPopupOnce", "true");

          // ✅ ENHANCED: Check for return URL from state OR window object
          let returnUrl = locationState?.returnUrl;
          let returnStateData = locationState?.returnState;

          if (!returnUrl && (window as any).__pending_deep_link_info) {
            const pendingInfo = (window as any).__pending_deep_link_info;
            returnUrl = pendingInfo.returnUrl;
            returnStateData = pendingInfo.returnState;
            console.log("🔄 Using restored deep link info from window object");
          }

          if (returnUrl && returnUrl.includes("/view-card/")) {
            console.log(
              "🔗 Onboarding complete, returning to deep link:",
              returnUrl
            );

            sessionStorage.removeItem("pending_deep_link");
            delete (window as any).__pending_deep_link_info;

            const cardIdMatch = returnUrl.match(/\/view-card\/(\d+)/);
            const cardId = cardIdMatch ? parseInt(cardIdMatch[1]) : null;

            if (cardId) {
              navigate("/shared-feedback-view", {
                replace: true,
                state: {
                  cardId: cardId,
                  fromDeepLink: true,
                  returnToDashboard: true,
                  developerEmail:
                    returnStateData?.developerEmail ||
                    localStorage.getItem("last_developer_email"),
                  activeTab: "all",
                  fromOnboarding: true,
                  workspaceId: workspaceId,
                  userId: userId,
                },
              });
              return;
            }
          }

          if (subdomain) {
            const extractedSubdomain =
              SubdomainManager.extractSubdomainFromUrl(subdomain);

            if (extractedSubdomain) {
              SubdomainManager.storeWorkspaceSubdomain(extractedSubdomain);
              console.log(`✅ Subdomain stored: ${extractedSubdomain}`);

              if (SubdomainManager.isLocalDevelopment()) {
                console.log(
                  "Localhost detected, navigating to /dashboard without subdomain change"
                );
                navigate("/dashboard", {
                  replace: true,
                  state: {
                    fromOnboarding: true,
                    workspaceId: workspaceId,
                    workspaceName: responseWorkspaceName,
                    subdomain: subdomain,
                    userId: userId,
                    icon_url: "",
                    workspace: result.data.workspace,
                    onboardingMessage: result.data.message,
                  },
                });
                return;
              }

              console.log(
                `🔄 Navigating to: https://${extractedSubdomain}.cluva.io/dashboard`
              );
              SubdomainManager.navigateToSubdomain(
                extractedSubdomain,
                "/dashboard"
              );
              return;
            }
          }

          console.log(
            "⚠️ No subdomain found, using fallback navigation to /dashboard"
          );
          navigate("/dashboard", {
            replace: true,
            state: {
              fromOnboarding: true,
              workspaceId: workspaceId,
              workspaceName: responseWorkspaceName,
              subdomain: subdomain,
              userId: userId,
              icon_url: "",
              workspace: result.data.workspace,
              onboardingMessage: result.data.message,
            },
          });
        } else {
          console.error("❌ Missing workspace ID or user ID");
          console.error("   - Workspace ID:", workspaceId);
          console.error("   - User ID:", userId);
          setError("Failed to create workspace. Please try again.");
        }
      } else {
        setError(result.message || "Failed to complete onboarding");
      }
    } catch (error) {
      console.error("Unexpected error:", error);
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleSkipIconSelection = async () => {
    setIsSubmitting(true);
    setError(null);

    try {
      const apiData = mapToApiFormat();
      console.log("📝 Submitting onboarding data (skipped icon):", apiData);

      const result = await ApiService.submitOnboarding(apiData);

      if (result.success && result.data) {
        console.log(
          "Onboarding completed successfully (icon skipped):",
          result.data
        );

        const workspaceId = result.data.workspace?.id;
        const userId = EnhancedTokenManager.getCurrentUserId();
        const responseWorkspaceName =
          result.data.workspace?.name || workspaceName.trim();
        const subdomain = result.data.workspace?.subdomain;

        if (workspaceId && userId) {
          console.log("🚀 Workspace created with ID:", workspaceId);
          console.log("👤 User ID extracted from token:", userId);

          localStorage.setItem("workspace_id", workspaceId.toString());

          console.log("⏭️ Icon selection skipped, no icon upload");

          const workspaceData = {
            id: workspaceId,
            name: responseWorkspaceName,
            subdomain: subdomain,
            icon_url: "",
          };

          const currentUserId = EnhancedTokenManager.getCurrentUserId();
          if (currentUserId) {
            EnhancedTokenManager.setWorkspaceDataForUser(
              currentUserId,
              workspaceData
            );

            const existingProfile = EnhancedTokenManager.getUserProfile();
            if (existingProfile) {
              const updatedProfile = {
                ...existingProfile,
                onboarded: true,
                workspace_id: workspaceId,
                workspace: workspaceData,
              };
              EnhancedTokenManager.setUserProfile(updatedProfile);
            }
          }

          EnhancedTokenManager.setOnboardingWorkspaceData({
            workspace: result.data.workspace,
            icon_url: "",
            user_id: userId,
          });

          localStorage.setItem("showBetaPopupOnce", "true");

          let returnUrl = locationState?.returnUrl;
          let returnStateData = locationState?.returnState;

          if (!returnUrl && (window as any).__pending_deep_link_info) {
            const pendingInfo = (window as any).__pending_deep_link_info;
            returnUrl = pendingInfo.returnUrl;
            returnStateData = pendingInfo.returnState;
            console.log("🔄 Using restored deep link info from window object");
          }

          if (returnUrl && returnUrl.includes("/view-card/")) {
            console.log(
              "🔗 Onboarding complete, returning to deep link:",
              returnUrl
            );

            sessionStorage.removeItem("pending_deep_link");
            delete (window as any).__pending_deep_link_info;

            const cardIdMatch = returnUrl.match(/\/view-card\/(\d+)/);
            const cardId = cardIdMatch ? parseInt(cardIdMatch[1]) : null;

            if (cardId) {
              navigate("/shared-feedback-view", {
                replace: true,
                state: {
                  cardId: cardId,
                  fromDeepLink: true,
                  returnToDashboard: true,
                  developerEmail:
                    returnStateData?.developerEmail ||
                    localStorage.getItem("last_developer_email"),
                  activeTab: "all",
                  fromOnboarding: true,
                  workspaceId: workspaceId,
                  userId: userId,
                },
              });
              return;
            }
          }

          if (subdomain) {
            const extractedSubdomain =
              SubdomainManager.extractSubdomainFromUrl(subdomain);

            if (extractedSubdomain) {
              SubdomainManager.storeWorkspaceSubdomain(extractedSubdomain);
              console.log(`✅ Subdomain stored: ${extractedSubdomain}`);

              if (SubdomainManager.isLocalDevelopment()) {
                console.log(
                  "Localhost detected, navigating to /dashboard without subdomain change"
                );
                navigate("/dashboard", {
                  replace: true,
                  state: {
                    fromOnboarding: true,
                    workspaceId: workspaceId,
                    workspaceName: responseWorkspaceName,
                    subdomain: subdomain,
                    userId: userId,
                    icon_url: "",
                    workspace: result.data.workspace,
                    onboardingMessage: result.data.message,
                  },
                });
                return;
              }

              console.log(
                `🔄 Navigating to: https://${extractedSubdomain}.cluva.io/dashboard`
              );
              SubdomainManager.navigateToSubdomain(
                extractedSubdomain,
                "/dashboard"
              );
              return;
            }
          }

          console.log(
            "⚠️ No subdomain found, using fallback navigation to /dashboard"
          );
          navigate("/dashboard", {
            replace: true,
            state: {
              fromOnboarding: true,
              workspaceId: workspaceId,
              workspaceName: responseWorkspaceName,
              subdomain: subdomain,
              userId: userId,
              icon_url: "",
              workspace: result.data.workspace,
              onboardingMessage: result.data.message,
            },
          });
        } else {
          console.error("❌ Missing workspace ID or user ID");
          console.error("   - Workspace ID:", workspaceId);
          console.error("   - User ID:", userId);
          setError("Failed to create workspace. Please try again.");
        }
      } else {
        setError(result.message || "Failed to complete onboarding");
      }
    } catch (error) {
      console.error("Unexpected error:", error);
      setError("An unexpected error occurred. Please try again.");
    } finally {
      setIsSubmitting(false);
    }
  };

  const handleContinue = () => {
    if (currentStep < 3) {
      setCurrentStep((prev) => prev + 1);
    }
  };

  const handleBack = () => {
    if (currentStep > 1) {
      setCurrentStep((prev) => prev - 1);
    }
  };

  const handleFileUpload = async (
    event: React.ChangeEvent<HTMLInputElement>
  ) => {
    const file = event.target.files?.[0];
    if (file) {
      if (!file.type.startsWith("image/")) {
        setError("Please select a valid image file");
        return;
      }

      if (file.size > 5 * 1024 * 1024) {
        setError("Image file size should be less than 5MB");
        return;
      }

      try {
        const fileUrl = await convertFileToUrl(file);
        setUploadedIcon(fileUrl);
        setSelectedIcon("uploaded");
        setError(null);
      } catch (error) {
        console.error("Error processing file:", error);
        setError("Error processing the uploaded file");
      }
    }
  };

  const handleUploadClick = () => {
    fileInputRef.current?.click();
  };

  const renderShimmer = () => {
    switch (currentStep) {
      case 1:
        return <Step1Shimmer />;
      case 2:
        return <Step2Shimmer />;
      case 3:
        return <Step3Shimmer />;
      default:
        return <Step1Shimmer />;
    }
  };

  const shouldShowShimmer = isInitializing;

  return (
    <div className="h-screen flex flex-col lg:flex-row bg-white" style={{ fontFamily }}>
      {/* Left Section - Main Content Area */}
      <div className="flex-1 lg:flex-none lg:w-1/2 flex flex-col justify-between bg-white order-2 lg:order-1">
        {/* Header with back button and logo */}
          <header className="flex items-center px-6 lg:px-8 xl:px-12 pt-6 lg:pt-10">
          {currentStep > 1 && (
            <button onClick={handleBack} className="text-gray-600 p-1 rounded-md">
              <div className="w-3 h-3  xl:w-4 xl:h-4 2xl:w-5 2xl:h-5 4xl:w-6 4xl:h-6">
                <BackButton className="w-full h-full" />
              </div>
            </button>
          )}
          <div className={currentStep > 1 ? "ml-2" : ""}>
            <div className="w-24 h-8 lg:w-24 lg:h-8  2xl:w-28 2xl:h-10 4xl:w-32 4xl:h-12">
              <Logo className="w-full h-full" />
            </div>
          </div>
        </header>


        {/* Main content area */}
        <main className="flex-1 flex items-start pl-6 lg:pl-8 xl:pl-12 py-4 pt-8 sm:pt-12 lg:pt-16 xl:pt-20 2xl:pt-28 3xl:pt-40 min-h-0 overflow-y-auto custom-scrollbar-overlay">
          <div className="w-full ">
            {shouldShowShimmer ? (
              renderShimmer()
            ) : (
              <>
                {/* Step 1: Usage and Role Selection */}
                {currentStep === 1 && (
                  <>
                    <h1 
                      className="text-2xl lg:text-3xl  2xl:text-4xl font-semibold text-[#1E2027] mb-[24px] leading-tight"
                      style={{ fontFamily }}
                    >
                      <span className="block">How are you planning to use</span>
                      <span className="block  leading-normal">
                        Cluva?
                      </span>
                    </h1>

                    <div className="flex flex-col sm:flex-row gap-4 sm:gap-6 mb-6 lg:mb-10 xl:mb-16">
                      <RadioOption
                        value="work"
                        label="For work"
                        selectedOption={selectedUsage}
                        onChange={(value) => {
                          setSelectedUsage(value);
                          setShowRoleOptions(true);
                        }}
                        fontFamily={fontFamily}
                      />
                      <RadioOption
                        value="personal"
                        label="For personal work"
                        selectedOption={selectedUsage}
                        onChange={(value) => {
                          setSelectedUsage(value);
                          setShowRoleOptions(true);
                        }}
                        fontFamily={fontFamily}
                      />
                    </div>

                    {showRoleOptions && (
                      <div className="mb-8">
                        <label 
                          className="block text-sm xl:text-base 3xl:text-xl font-medium text-[#424650] mb-4"
                          style={{ fontFamily }}
                        >
                          What is your role?
                        </label>
                        <div className="flex flex-wrap gap-3 max-w-2xl">
                          {roleOptions.map((role) => (
                            <button
                              key={role}
                              onClick={() => setSelectedRole(role)}
                              className={`px-4 py-2 text-sm xl:text-base 3xl:text-xl font-medium rounded-[10px]  transition-all ${
                                selectedRole === role
                                  ? "bg-[#F2F9FF] text-[#202529] border-[1px] border-[#6BA4D8]"
                                  : "bg-white text-[#202529] border-[#B7BBCA] border-[0.5px] hover:border-[#6BA4D8]"
                              }`}
                              style={{ fontFamily }}
                            >
                              {role}
                            </button>
                          ))}
                        </div>
                      </div>
                    )}
                  </>
                )}

                {/* Step 2: Workspace Naming */}
                {currentStep === 2 && (
                  <>
                    <h1 
                      className="text-2xl lg:text-3xl 2xl:text-4xl font-semibold text-[#1E2027] mb-2"
                      style={{ fontFamily }}
                    >
                      Name your workspace
                    </h1>
                    <p 
                      className="text-sm xl:text-lg 3xl:text-xl max-w-lg text-[#1E2027] mb-4 lg:mb-8 xl:mb-[58px]"
                      style={{ fontFamily }}
                    >
                      Your workspace name is what will be visible to all
                      collaborators participating in sharing feedback
                    </p>

                    <input
                      type="text"
                      placeholder="Type your workspace name"
                      value={workspaceName}
                      onChange={(e) => {
                        const value = e.target.value;
                        const capitalizedValue = value
                          .split(" ")
                          .map((word) => {
                            if (word.length === 0) return word;
                            return (
                              word.charAt(0).toUpperCase() +
                              word.slice(1).toLowerCase()
                            );
                          })
                          .join(" ");
                        setWorkspaceName(capitalizedValue);
                      }}
                      className="w-[400px] 3xl:w-[500px] px-3 py-2.5 text-base xl:text-base 2xl:text-lg 3xl:text-xl font-inter border border-[#050505] rounded-lg mb-6 focus:outline-none focus:ring-1 focus:ring-[#7ABFFF] focus:border-transparent"
                    
                    />

                    <div className="mb-4">
                      <div className="relative w-[400px] 3xl:w-[500px]">
                        <div className="relative bg-[#ECF2F7] border rounded-lg px-3 py-2.5  flex items-center focus:outline-none  ">
                          <div className="flex-1 flex items-center whitespace-nowrap overflow-hidden">
                            <input
                              type="text"
                              value={subdomain}
                              onChange={(e) => {
                                const value = e.target.value
                                  .toLowerCase()
                                  .replace(/[^a-z0-9_]/g, "");
                                setSubdomain(value);
                                setWorkspaceNameAvailable(null);
                              }}
                              placeholder="yourworkspace"
                              disabled={isCheckingWorkspace}
                              className="bg-transparent outline-none text-base xl:text-base 2xl:text-lg 3xl:text-xl font-inter text-[#1E1F26] placeholder-[#7D7D7D] font-medium flex-shrink min-w-0 p-0 m-0 caret-[#050505]"
                              
                            />
                          </div>
                          <span 
                            className="text-base xl:text-lg 2xl:text-xl 3xl:text-2xl text-[#1E1F26] font-medium select-none flex-shrink-0 ml-auto"
                            style={{ fontFamily }}
                          >
                            .{baseDomain}
                          </span>
                        </div>
                      </div>

                      <div className="mt-3 flex items-center gap-2">
                        {isCheckingWorkspace ? (
                          <>
                            <div className="w-8 h-8 2xl:w-10 2xl:h-10 -mt-2 ">
                              <Lottie
                                animationData={DotLoader}
                                loop={true}
                                style={{
                                  width: "100%",
                                  height: "100%",
                                  filter: "brightness(0)",
                                }}
                              />
                            </div>
                          </>
                        ) : workspaceNameAvailable !== null &&
                          subdomain.trim().length > 0 ? (
                          workspaceNameAvailable ? (
                            <>
                              <Check className="w-3 h-3 2xl:w-4 2xl:h-4 " />
                              <p 
                                className="text-[12px] 2xl:text-[14px] 3xl:text-[16px] text-[#19CC46]"
                                style={{ fontFamily }}
                              >
                                Available
                              </p>
                            </>
                          ) : (
                            <>
                              <Cancel className="w-3 h-3 2xl:w-4 2xl:h-4" />
                              <p 
                                className="text-[12px] 2xl:text-[14px] 3xl:text-[16px] text-[#FF3B30]"
                                style={{ fontFamily }}
                              >
                                URL is not available
                              </p>
                            </>
                          )
                        ) : null}
                      </div>
                    </div>
                  </>
                )}

                {/* Step 3: Icon Selection */}
                {currentStep === 3 && (
                  <>
                    <h1 
                      className="text-2xl lg:text-3xl  2xl:text-4xl font-semibold text-[#1E2027] mb-2"
                      style={{ fontFamily }}
                    >
                      Select workspace icon
                    </h1>
                    <p 
                      className="text-sm xl:text-lg 3xl:text-xl text-[#1E2027] mb-4 lg:mb-8 xl:mb-[58px]"
                      style={{ fontFamily }}
                    >
                      Choose an icon which suits best for your workspace
                    </p>

                    <div className="flex items-center gap-4 mb-6">
                      {/* Static Icons Grid */}
                      <div className="grid grid-cols-4 gap-4">
                        {pngIcons.map(({ src, id }) => (
                          <div
                            key={id}
                            onClick={() => {
                              setSelectedIcon(id);
                            }}
                            className={`w-12 h-12 2xl:w-16 2xl:h-16 rounded-[14px] flex items-center justify-center  ${
                              selectedIcon === id
                                ? "border-2 border-[#1E1F26] p-[5px] "
                                : ""
                            }  cursor-pointer  `}
                          >
                            <img
                              src={src}
                              alt={id}
                              className="w-12 h-12 2xl:w-16 2xl:h-16 object-contain"
                            />
                          </div>
                        ))}
                      </div>

                      {/* Vertical Separator */}
                      <div className="h-12 2xl:h-16 w-[1px] bg-[#E9E9E9]"></div>

                      {/* Upload Icon */}
                      {uploadedIcon ? (
                        <div
                          className="relative w-12 h-12 2xl:w-16 2xl:h-16 rounded-[14px] flex items-center justify-center cursor-pointer transition-colors group bg-white shadow-sm"
                          onClick={() => {
                            setSelectedIcon("uploaded");
                          }}
                        >
                          <img
                            src={uploadedIcon}
                            alt="Uploaded"
                            className={`w-full h-full object-contain rounded-[14px] ${
                              selectedIcon === "uploaded"
                                ? "  border-2 border-[#1E1F26] p-[5px]  "
                                : " "
                            }`}
                          />
                          <div
                            className="absolute inset-0 bg-black bg-opacity-50 rounded-xl flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity"
                            onClick={(e) => {
                              e.stopPropagation();
                              setUploadedIcon(null);
                              setSelectedIcon(null);
                              if (fileInputRef.current) {
                                fileInputRef.current.value = "";
                              }
                            }}
                          >
                            <UndoIcon className="w-4 h-4 2xl:w-6 2xl:h-6 text-white" />
                          </div>
                        </div>
                      ) : (
                        <div
                          onClick={handleUploadClick}
                          className="w-12 h-12 2xl:w-16 2xl:h-16 rounded-[14px] border  border-[#4991EA] hover:border-blue-400 flex items-center justify-center cursor-pointer transition bg-[#F3F6FF]"
                        >
                          <UploadIcon className="w-5 h-5 2xl:w-6 2xl:h-6 text-blue-500" />
                        </div>
                      )}
                    </div>

                    <input
                      ref={fileInputRef}
                      type="file"
                      accept="image/*"
                      onChange={handleFileUpload}
                      className="hidden"
                    />
                  </>
                )}
              </>
            )}
          </div>
        </main>

        {/* Footer with navigation controls */}
        <footer className="px-6 lg:px-8 xl:px-12 pb-4 lg:pb-8 xl:pb-10">
          <div className="max-w-md lg:max-w-lg xl:max-w-xl">
            {currentStep === 3 ? (
              <div className="flex items-center gap-4 mb-8">
                {!isSubmitting ? (
                  <>
                    <Button
                      variant={canContinue ? "primary" : "secondary"}
                      disabled={!canContinue}
                      className="flex items-center justify-center px-6 py-2.5 text-sm xl:text-base 2xl:text-lg 3xl:text-xl"
                      onClick={handleOnboardingComplete}
                      style={{ fontFamily }}
                    >
                      Save & Continue <ArrowRight className="ml-2 w-5 h-5" />
                    </Button>
                    <Button
                      variant="ghost"
                      className="px-6 py-2.5 text-sm xl:text-base 2xl:text-lg 3xl:text-xl"
                      onClick={handleSkipIconSelection}
                      style={{ fontFamily }}
                    >
                      Skip
                    </Button>
                  </>
                ) : (
                  <Button
                    variant="primary"
                    className="flex items-center justify-center px-6 py-2.5 text-sm xl:text-base 2xl:text-lg 3xl:text-xl cursor-wait"
                    style={{ fontFamily }}
                  >
                    <span
                      className="inline-block relative"
                      style={{ width: 60, height: 20 }}
                    >
                      <Lottie
                        animationData={DotLoader}
                        loop={true}
                        style={{
                          width: 120,
                          height: 48,
                          position: "absolute",
                          top: "50%",
                          left: "50%",
                          transform: "translate(-50%, -50%)",
                        }}
                      />
                    </span>
                  </Button>
                )}
              </div>
            ) : (
              <Button
                variant={canContinue && !isSubmitting && !shouldShowShimmer ? "primary" : "secondary"}
                disabled={!canContinue || isSubmitting || shouldShowShimmer}
                className="flex items-center px-6 py-2.5 text-sm xl:text-base 2xl:text-lg 3xl:text-xl mb-8"
                onClick={handleContinue}
                style={{ fontFamily }}
              >
                Continue <ArrowRight className="ml-2 w-5 h-5" />
              </Button>
            )}


            {/* Progress indicator bars */}
            <div className="flex justify-start space-x-2">
              {[1, 2, 3].map((step, index) => {
                const isActive = currentStep >= step;
                return (
                  <div
                    key={index}
                    className={`relative w-[96px] 2xl:w-[110px] h-1.5 rounded-full bg-[#E3EAFD] overflow-hidden`}
                  >
                    <div
                      className={`absolute left-0 top-0 h-full rounded-full bg-[#379744] transition-all duration-700 ease-in-out`}
                      style={{
                        width: isActive ? "100%" : "0%",
                      }}
                    />
                  </div>
                );
              })}
            </div>
          </div>
        </footer>
      </div>

      {/* Right Section - Decorative Image Area / Workspace Preview */}
      <div className="w-full lg:w-1/2 relative overflow-hidden min-h-48 sm:min-h-64 lg:min-h-screen order-1 lg:order-2">
        {currentStep === 1 ? (
          <div className="absolute inset-0 w-full h-full">
            <OnboardingImage className="w-full h-full object-cover object-right scale-110 translate-x-8" />
          </div>
        ) : (
          <div className="absolute inset-0 w-full h-full bg-gradient-to-br from-[#EEF2F6] via-[#E8EEF7] to-[#DCE8F5] flex items-center justify-end">
            <div className="bg-white rounded-l-2xl shadow-sm h-full lg:h-[500px] xl:h-[500px] 2xl:h-[550px] w-full max-w-lg lg:max-w-xl xl:max-w-2xl 3xl:max-w-3xl 4xl:max-w-5xl ml-4 sm:ml-8 flex overflow-hidden">
              {/* Left Sidebar inside white container */}
              <div className="flex flex-col items-center gap-4 lg:gap-6 pt-4 lg:pt-6 px-4  border-r border-gray-100">
                {/* Workspace Icon at top */}
                {currentStep === 3 && (selectedIcon || uploadedIcon) ? (
                  selectedIcon === "uploaded" && uploadedIcon ? (
                    <div className="w-6 h-6 lg:w-8 lg:h-8 rounded-lg overflow-hidden flex-shrink-0  ">
                      <img
                        src={uploadedIcon}
                        alt="Workspace icon"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : selectedIcon ? (
                    <div className="w-6 h-6 lg:w-8 lg:h-8 rounded-lg overflow-hidden flex-shrink-0  ">
                      <img
                        src={
                          pngIcons.find((icon) => icon.id === selectedIcon)?.src
                        }
                        alt="Workspace icon"
                        className="w-full h-full object-cover"
                      />
                    </div>
                  ) : (
                    <div className="w-6 h-6 lg:w-8 lg:h-8  rounded-lg flex items-center justify-center flex-shrink-0  ">
                      <Workspace className="w-full h-full text-white" />
                    </div>
                  )
                ) : (
                  <div className="w-6 h-6 lg:w-8 lg:h-8 rounded-lg flex items-center justify-center flex-shrink-0 ">
                    <Workspace className="w-full h-full text-white" />
                  </div>
                )}
                <div className="w-full  h-[1px] bg-[#E4E4E4] -mt-3 mx-auto "></div>
                <div className="w-4 h-4 lg:w-5 lg:h-5 flex items-center justify-center ">
                  <Home className="w-full h-full object-cover " />
                </div>
                <div className="w-4 h-4 lg:w-5 lg:h-5 flex items-center justify-center">
                  <Folder className="w-full h-full object-cover " />
                </div>
                <div className="relative w-4 h-4 lg:w-5 lg:h-5 flex items-center justify-center">
                  <div className="absolute inset-0 flex items-center justify-center">
                    <div className="bg-[#F1F2F4] rounded-[6px] w-full h-full p-4"></div>
                  </div>
                  <Setting className="relative w-full h-full object-cover z-10" />
                </div>
                <div className="w-4 h-4 lg:w-5 lg:h-5 flex items-center justify-center">
                  <Logout className="w-full h-full object-cover " />
                </div>
              </div>

              {/* Main Content Area */}
              <div className="flex-1 px-4 lg:px-5 xl:px-6 py-6 lg:py-8 xl:py-8">
                <div className="mb-8 lg:mb-10 border-b border-[#DEDEDE] pb-4 -mr-5 lg:-mr-6 xl:-mr-8">
                  <h2 className="text-lg lg:text-xl xl:text-2xl font-semibold text-gray-900"
                   style={{ fontFamily }}
                   >
                    Workspace details
                  </h2>
                </div>
                <div className=" bg-[#FCFDFE] border-[0.5px] border-[#C4D1F5] rounded-[10px] p-5">
                  <div className="mb-5 lg:mb-6">
                    <label className="block text-xs font-medium text-gray-500 mb-2 uppercase tracking-wide"
                     style={{ fontFamily }}
                     >
                      Workspace name
                    </label>
                    <div className="px-3 lg:px-4 py-2.5 lg:py-3 bg-white border border-gray-200 rounded-lg">
                      <span className="text-xs lg:text-sm text-gray-900"
                       style={{ fontFamily }}
                       >
                        {workspaceName.trim() || "Workspace name"}
                      </span>
                    </div>
                  </div>

                  <div>
                    <label className="block text-xs font-medium text-gray-500 mb-2 uppercase tracking-wide">
                      Workspace url
                    </label>
                    <div className="px-3 lg:px-4 py-2.5 lg:py-3 bg-white border border-gray-200 rounded-lg">
                      <span className="text-xs lg:text-sm"
                       style={{ fontFamily }}
                      >
                        <span className="text-gray-400">https://</span>
                        <span className="text-gray-900">
                          {subdomain.trim() ? subdomain : "yourworkspace"}
                        </span>
                        <span className="text-gray-900">.{baseDomain}</span>
                      </span>
                    </div>
                  </div>
                </div>
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
  );
};

export default OnboardingScreen;
